/**
 * Home — one-page-two-doors layout.
 *
 * Rebuilt Feb 2026 (Iter 118) around a clear "employer vs talent" split so
 * a first-time visitor picks a door in under 2 seconds. Every trademark /
 * jargon term the old page carried was replaced with plain-English copy.
 *
 * Sections:
 *   1. Sticky nav (rendered globally by <Navbar />)
 *   2. Hero — dual CTA, both audiences addressed in the subhead
 *   3. How It Works — split (2-col desktop, tabs mobile)
 *   4. Social Proof — split with clean empty state (no fake quotes)
 *   5. Trust — verification + privacy + data protection (shared)
 *   6. Final CTA band — two side-by-side conversion panels
 *
 * Existing routes preserved: /employers, /employers/signup, /talent,
 * /talent/signup, /pricing, /success-stories, /trust-center, /privacy,
 * /terms, /cookies, /dpa, /subprocessors, /thank-you-employer,
 * /thank-you-talent.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Briefcase,
    Users,
    Search,
    UserCheck,
    Handshake,
    Lock,
    ShieldCheck,
    Quote,
    Sparkles,
    Building2,
    IdCard,
    CheckCircle2,
} from "lucide-react";
import { TID } from "@/lib/testIds";
import LiveJobsTicker from "@/components/home/LiveJobsTicker";
import RecentlyHiredRibbon from "@/components/home/RecentlyHiredRibbon";
import FoundingChapterBand from "@/components/home/FoundingChapterBand";
import VerificationStrip from "@/components/home/VerificationStrip";
import RealTestimonials from "@/components/home/RealTestimonials";
import BlogTeaser from "@/components/home/BlogTeaser";

/* -------------------------------------------------------------------------- */
/* SECTION 2 — HERO                                                           */
/* -------------------------------------------------------------------------- */

const Hero = () => (
    <section className="relative overflow-hidden pt-20 pb-14 sm:pt-24 sm:pb-20 lg:pt-32" data-testid="home-hero">
        <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        <div className="relative container-page">
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl mx-auto text-center"
            >
                <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-emerald-700 dark:text-emerald-400 bg-accent px-4 py-1.5 rounded-full border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    The hiring marketplace — not another resume tool.
                </span>
                <h1 className="mt-6 font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground leading-[1.05]">
                    Be the profile they find,{" "}
                    <span className="text-brand-green dark:text-emerald-400">not the resume they skip.</span>
                </h1>
                <div className="mt-6 space-y-2 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                    <p data-testid="home-hero-sub-employer">
                        <b className="text-foreground">Employers:</b> post a role and meet professionals with a Verified Talent Passport — a phone-verified profile from a real person who has attested they are authorized to work in the US, so you&apos;re not vetting raw resumes from scratch.
                    </p>
                    <p data-testid="home-hero-sub-talent">
                        <b className="text-foreground">Professionals:</b> build your Talent Passport once and get discovered by real employers hiring now — no more spraying resumes into inboxes.
                    </p>
                </div>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                    <div className="flex flex-col gap-1">
                        <Link
                            to="/employers"
                            data-testid={TID.home.heroHireTalent}
                            className="btn-green w-full justify-center inline-flex items-center gap-2"
                        >
                            I&apos;m hiring <ArrowRight size={16} />
                        </Link>
                        <p className="text-xs text-muted-foreground text-center" data-testid="home-hero-finepr-employer">
                            Free to try. <Link to="/pricing" className="underline hover:text-foreground">See pricing</Link>.
                        </p>
                    </div>
                    <div className="flex flex-col gap-1">
                        <Link
                            to="/talent"
                            data-testid={TID.home.heroFindJob}
                            className="btn-navy w-full justify-center inline-flex items-center gap-2"
                        >
                            I&apos;m job seeking <ArrowRight size={16} />
                        </Link>
                        <p className="text-xs text-muted-foreground text-center" data-testid="home-hero-finepr-talent">
                            Free to try. No credit card. No spam.
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

/* -------------------------------------------------------------------------- */
/* SECTION 3 — HOW IT WORKS (split; desktop 2-col, mobile tabs)               */
/* -------------------------------------------------------------------------- */

const employerSteps = [
    { icon: Briefcase, title: "Post your job", body: "Paste a link or upload a description. It takes about a minute." },
    { icon: Search, title: "Meet verified professionals", body: "AI matching surfaces real people from the Simonara Talent Passport™ pool — a Talent Passport is a checked, standardised profile so you're not vetting raw resumes." },
    { icon: Handshake, title: "Interview and hire", body: "Message directly, book interviews, and make an offer when you\u2019re ready." },
];

const talentSteps = [
    { icon: IdCard, title: "Build your Talent Passport", body: "A checked, standardised profile you build once and reuse across every employer on Simonara. Free." },
    { icon: Users, title: "Get discovered by real employers", body: "You choose who can see you. Employers only reach out with intent — no cold spray." },
    { icon: CheckCircle2, title: "Get hired", body: "Chat, interview, and land the offer — all in one place." },
];

const StepCard = ({ step, index, testPrefix, accent }) => {
    const Icon = step.icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: index * 0.08 }}
            className="rounded-2xl border border-border bg-card p-6"
            data-testid={`${testPrefix}-${index + 1}`}
        >
            <div className="flex items-center justify-between">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
                    <Icon size={20} />
                </div>
                <span className="mono-score text-2xl font-bold text-border">{`0${index + 1}`}</span>
            </div>
            <h3 className="mt-4 font-heading font-semibold text-base text-foreground">{step.title}</h3>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
        </motion.div>
    );
};

const HowItWorksColumn = ({ audience, steps, testPrefix, accent, cta }) => (
    <div>
        <div className="flex items-center gap-2">
            <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${accent.pill}`}>
                {audience}
            </div>
        </div>
        <div className="mt-3 space-y-3">
            {steps.map((s, i) => (
                <StepCard key={s.title} step={s} index={i} testPrefix={testPrefix} accent={accent.icon} />
            ))}
        </div>
        <Link
            to={cta.to}
            data-testid={`${testPrefix}-cta`}
            className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${accent.link} hover:underline`}
        >
            {cta.label} <ArrowRight size={14} />
        </Link>
    </div>
);

const HowItWorksSplit = () => {
    const [mobileTab, setMobileTab] = useState("employer");
    const employerAccent = {
        pill: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30",
        icon: "bg-accent text-emerald-600 dark:text-emerald-400",
        link: "text-emerald-700 dark:text-emerald-400",
    };
    const talentAccent = {
        pill: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/30",
        icon: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
        link: "text-sky-700 dark:text-sky-400",
    };
    return (
        <section className="section-pad bg-muted/40" data-testid="home-how-it-works">
            <div className="container-page">
                <div className="max-w-2xl mx-auto text-center">
                    <p className="overline">How it works</p>
                    <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                        Two doors. One outcome: a great hire.
                    </h2>
                    <p className="mt-3 text-muted-foreground">Pick your side — the flow&apos;s built for both.</p>
                </div>

                {/* Mobile tabs */}
                <div className="mt-8 lg:hidden">
                    <div className="inline-flex rounded-full bg-card border border-border p-1 w-full max-w-sm mx-auto" role="tablist" data-testid="how-mobile-tabs">
                        <button
                            role="tab"
                            aria-selected={mobileTab === "employer"}
                            data-testid="how-mobile-tab-employer"
                            onClick={() => setMobileTab("employer")}
                            className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${mobileTab === "employer" ? "bg-brand-navy text-white" : "text-foreground"}`}
                        >
                            For Employers
                        </button>
                        <button
                            role="tab"
                            aria-selected={mobileTab === "talent"}
                            data-testid="how-mobile-tab-talent"
                            onClick={() => setMobileTab("talent")}
                            className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${mobileTab === "talent" ? "bg-brand-navy text-white" : "text-foreground"}`}
                        >
                            For Talent
                        </button>
                    </div>
                    <div className="mt-6">
                        {mobileTab === "employer" ? (
                            <HowItWorksColumn
                                audience="For Employers"
                                steps={employerSteps}
                                testPrefix="how-emp-mobile-step"
                                accent={employerAccent}
                                cta={{ to: "/employers/signup", label: "Post a job" }}
                            />
                        ) : (
                            <HowItWorksColumn
                                audience="For Talent"
                                steps={talentSteps}
                                testPrefix="how-tal-mobile-step"
                                accent={talentAccent}
                                cta={{ to: "/talent/signup", label: "Build my free profile" }}
                            />
                        )}
                    </div>
                </div>

                {/* Desktop two columns */}
                <div className="mt-12 hidden lg:grid grid-cols-2 gap-8" data-testid="how-desktop-grid">
                    <HowItWorksColumn
                        audience="For Employers"
                        steps={employerSteps}
                        testPrefix="how-emp-step"
                        accent={employerAccent}
                        cta={{ to: "/employers/signup", label: "Post a job" }}
                    />
                    <HowItWorksColumn
                        audience="For Talent"
                        steps={talentSteps}
                        testPrefix="how-tal-step"
                        accent={talentAccent}
                        cta={{ to: "/talent/signup", label: "Build my free profile" }}
                    />
                </div>
            </div>
        </section>
    );
};

/* -------------------------------------------------------------------------- */
/* SECTION 4 — SOCIAL PROOF (split, empty state, no fake quotes)              */
/* -------------------------------------------------------------------------- */

const EmptyQuoteCard = ({ audience, testId }) => (
    <div className="rounded-2xl border border-dashed border-border bg-card p-6 min-h-[168px] flex flex-col justify-between" data-testid={testId}>
        <Quote size={18} className="text-muted-foreground/60" />
        <div>
            <p className="text-sm text-muted-foreground italic">
                Real {audience} testimonials live here as they come in — we don&apos;t invent quotes.
            </p>
            <p className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground/70">Placeholder</p>
        </div>
    </div>
);

const LogoTile = ({ label, testId }) => (
    <div className="rounded-xl border border-dashed border-border bg-card px-4 py-5 flex items-center justify-center text-center" data-testid={testId}>
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/80">
            {label}
        </span>
    </div>
);

const SocialProofSplit = () => (
    <section className="section-pad" data-testid="home-social-proof">
        <div className="container-page">
            <div className="max-w-2xl mx-auto text-center">
                <p className="overline">Social proof</p>
                <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                    Trusted by both sides of the hire.
                </h2>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Employer column */}
                <div className="rounded-3xl border border-border bg-card p-7" data-testid="social-proof-employer-col">
                    <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                            <Building2 size={11} /> Employers
                        </div>
                    </div>
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">Companies hiring on Simonara</h3>
                    <div className="mt-5 grid grid-cols-3 gap-3" data-testid="social-proof-employer-logos">
                        {["Founding Partner", "Coming soon", "Coming soon", "Coming soon", "Coming soon", "Coming soon"].map((l, i) => (
                            <LogoTile key={i} label={l} testId={`social-proof-employer-logo-${i}`} />
                        ))}
                    </div>
                    <div className="mt-5">
                        <EmptyQuoteCard audience="employer" testId="social-proof-employer-quote" />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <Link to="/success-stories" className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1" data-testid="social-proof-employer-stories-link">
                            Read success stories <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>

                {/* Talent column */}
                <div className="rounded-3xl border border-border bg-card p-7" data-testid="social-proof-talent-col">
                    <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/30">
                            <IdCard size={11} /> Talent
                        </div>
                    </div>
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">Recent talent outcomes</h3>
                    <div className="mt-5 grid grid-cols-3 gap-3" data-testid="social-proof-talent-outcomes">
                        {[
                            { k: "Verified profiles", v: "Growing" },
                            { k: "Placement time", v: "Days, not months" },
                            { k: "Cost to talent", v: "Free" },
                        ].map((tile) => (
                            <div key={tile.k} className="rounded-xl border border-border bg-muted/40 px-3 py-4 text-center">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{tile.k}</div>
                                <div className="mt-1 text-sm font-heading font-semibold text-foreground">{tile.v}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5">
                        <EmptyQuoteCard audience="talent" testId="social-proof-talent-quote" />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <Link to="/success-stories" className="text-sm font-semibold text-sky-700 dark:text-sky-400 hover:underline inline-flex items-center gap-1" data-testid="social-proof-talent-stories-link">
                            Read success stories <ArrowRight size={12} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* -------------------------------------------------------------------------- */
/* SECTION 5 — TRUST SECTION (shared, plain-English)                          */
/* -------------------------------------------------------------------------- */

const trustPillars = [
    {
        icon: UserCheck,
        title: "Signup verification, in three steps",
        body: "At signup every talent passes a US mobile SMS code, a work-authorization attestation, and a US state + ZIP check. Employers can also request government-ID verification on any candidate. The candidate completes a secure ID check; the employer sees only the verified outcome — never the ID document.",
        link: { to: "/verification", label: "How verification works" },
    },
    {
        icon: Lock,
        title: "Your data stays yours",
        body: "We never sell personal information. Sensitive fields are encrypted, admin views are masked by default, and you can request access, export, or deletion anytime.",
        link: { to: "/privacy", label: "Privacy policy" },
    },
];

const TrustSection = () => (
    <section className="section-pad bg-muted/40" data-testid="home-trust">
        <div className="container-page">
            <div className="max-w-2xl mx-auto text-center">
                <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Trust</p>
                <h2 className="mt-3 font-heading font-semibold text-3xl sm:text-4xl tracking-tight text-foreground">
                    Verification. Privacy. Your data — in plain English.
                </h2>
                <p className="mt-3 text-muted-foreground">
                    No dark patterns. No jargon. Just the guardrails you&apos;d want on both sides of a hire.
                </p>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                {trustPillars.map((p, i) => (
                    <motion.div
                        key={p.title}
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ delay: i * 0.08 }}
                        className="rounded-2xl border border-border bg-card p-6"
                        data-testid={`trust-pillar-${i + 1}`}
                    >
                        <div className="w-11 h-11 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <p.icon size={20} />
                        </div>
                        <h3 className="mt-4 font-heading font-semibold text-lg text-foreground">{p.title}</h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                        <Link to={p.link.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline" data-testid={`trust-pillar-${i + 1}-link`}>
                            {p.link.label} <ArrowRight size={12} />
                        </Link>
                    </motion.div>
                ))}
            </div>
            <div className="mt-8 text-center">
                <Link to="/trust-center" className="btn-outline inline-flex items-center gap-2" data-testid="home-trust-full-link">
                    Explore the Trust Center <ArrowRight size={14} />
                </Link>
            </div>
        </div>
    </section>
);

/* -------------------------------------------------------------------------- */
/* SECTION 6 — FINAL CTA BAND (two panels)                                    */
/* -------------------------------------------------------------------------- */

const FinalCtaBand = () => (
    <section className="section-pad" data-testid="home-final-cta">
        <div className="container-page">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Employer panel */}
                <div
                    data-testid={TID.home.finalCtaEmployer}
                    className="relative overflow-hidden rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 via-background to-emerald-500/5 p-8 sm:p-10 flex flex-col"
                >
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider w-fit">
                        <Sparkles size={11} /> Founding Employer Pilot
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Hiring? Two ways to get started.
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                        Run the hire yourself with a <b className="text-foreground">Starter or Growth subscription</b> (14-day free trial, credit card required to start), <b className="text-foreground">or</b> book a <b className="text-foreground">1:1 with Simonara Recruit™</b> — a real recruiter sources, screens, and shortlists for you. First 10 founding employers lock in a <b className="text-foreground">12% Recruit placement fee</b>, forever.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2">
                        <Link
                            to="/employers/signup?source=home_final_cta"
                            data-testid="home-final-cta-employer-btn"
                            className="btn-green inline-flex items-center gap-2"
                        >
                            Start free trial <ArrowRight size={14} />
                        </Link>
                        <Link
                            to="/recruiting?source=recruit_cta"
                            data-testid="home-final-cta-recruit-btn"
                            className="btn-outline inline-flex items-center gap-2"
                        >
                            Talk to Recruit™
                        </Link>
                    </div>
                </div>

                {/* Talent panel */}
                <div
                    data-testid={TID.home.finalCtaSeeker}
                    className="relative overflow-hidden rounded-3xl border border-sky-500/40 bg-gradient-to-br from-sky-500/10 via-background to-sky-500/5 p-8 sm:p-10 flex flex-col"
                >
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/15 text-sky-700 dark:text-sky-400 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider w-fit">
                        <IdCard size={11} /> Free forever
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Job hunting? Build your free profile.
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
                        Get discovered by verified employers hiring in your field. You choose who sees you.
                    </p>
                    <div className="mt-6">
                        <Link
                            to="/talent/signup"
                            data-testid="home-final-cta-talent-btn"
                            className="btn-navy inline-flex items-center gap-2"
                        >
                            Get started <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const Home = () => {
    return (
        <div className="bg-background" data-testid="home-page">
            <Hero />
            <VerificationStrip />
            <LiveJobsTicker />
            <RecentlyHiredRibbon />
            <HowItWorksSplit />
            <FoundingChapterBand />
            <RealTestimonials />
            <BlogTeaser />
            <TrustSection />
            <FinalCtaBand />
        </div>
    );
};

export default Home;
